import { EventType } from "@/analytics/analytics-events";
import { useSavedView } from "@/features/saved-views/saved-view-context";
import { useHasDraftInProgress } from "@/hooks/use-has-draft-in-progress";
import { usePollNewMergedRevision } from "@/hooks/use-poll-new-merged-revision";
import {
  PROJECT_OUTDATED_MESSAGE,
  useProjectStatus,
} from "@/hooks/use-project-status";
import { useDraftInProgressBannerProps } from "@/modes/create-area-mode/create-area-mode-overlay";
import {
  useCurrentAreaIfAvailable,
  useIsCurrentAreaLoading,
} from "@/modes/mode-data-context";
import { changeMode } from "@/store/mode-slice";
import { selectActiveArea } from "@/store/selections-selectors";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import {
  selectAreSubscriptionsAvailable,
  selectCanReadPointCloud,
} from "@/store/subscription-features/subscription-features-selectors";
import { useIsReportToolActive } from "@/store/ui/ui-utils";
import { selectCurrentUser } from "@/store/user-selectors";
import {
  useDashboardAccountUrl,
  useDashboardAddOnUrl,
} from "@/utils/redirects";
import {
  Banner,
  BannerProps,
  BannerVariants,
  FaroText,
} from "@faro-lotv/flat-ui";
import { Analytics, isInternalUserEmail } from "@faro-lotv/foreign-observers";
import {
  GUID,
  IElementSection,
  isIElementPointCloudStream,
} from "@faro-lotv/ielement-types";
import {
  selectAreaCaptureTreeDataSetIds,
  selectIsFloorWithoutScale,
  selectIsSubtreeLoaded,
  selectLoadedIElements,
  selectProjectId,
} from "@faro-lotv/project-source";
import { ProjectStatus } from "@faro-lotv/service-wires";
import { Link } from "@mui/material";
import { Stack } from "@mui/system";
import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import {
  disableScaleAreaMessage,
  useDisableCaptureTreeAlignment,
} from "./tree/cad-model-tree/use-disable-capture-tree-alignment";

/** Context information passed to a banner to check if it should be visible */
type AppContextForBanner = {
  /** Id of the current active area */
  activeArea: IElementSection | undefined;
};

type BannerData = BannerProps & {
  /** Unique id for this banner, to not add the same banner multiple times */
  bannerId: GUID;
  /**
   * Priority of this banner against other banners of the same variant, counting from 0 which is
   * the highest priority. So 0 has higher priority than 1, which has higher priority than 2, and
   * so on. The banner with the highest priority gets displayed.
   */
  priority: number;
  /**
   * A function to check if the banner should be visible in a specific application state
   *
   * If not define the banner will always be considered to be showed
   *
   * @returns true if this banner should be visible in the current app state
   */
  shouldShow?(context: AppContextForBanner): boolean;
};

type AddBannerFunction = (banner: BannerData) => void;
type RemoveBannerFunction = (bannerId: GUID) => void;

/** @returns an unique AppBanner showing the most important message until it's dismissed */
export function AppBanner(): JSX.Element | null {
  const [banners, setBanners] = useState<BannerData[]>([]);

  /** Callback to add a banner to the stack of banners, keeping them ordered by importance */
  const addBanner = useCallback<AddBannerFunction>((banner) => {
    setBanners((banners) =>
      // Do not add the same banner twice.
      banners.some(({ bannerId }) => bannerId === banner.bannerId)
        ? banners
        : [...banners, banner].sort(sortBanners),
    );
  }, []);

  /** Callback to remove the banner of given id from the stack */
  const removeBanner = useCallback<RemoveBannerFunction>((id) => {
    setBanners((banners) => banners.filter((banner) => banner.bannerId !== id));
  }, []);

  useCheckForOutdatedProject(addBanner);
  useCheckForNewMergedRevision(addBanner);
  useCheckForMissingPCMBundle(addBanner);
  useCheckForUnscaledFloor(addBanner);
  useCheckForEmptyArea(addBanner, removeBanner);
  useCheckForValidSavedView(addBanner);
  useCheckForTooManyCookies(addBanner);
  useCheckForDraftInProgress(addBanner);

  const activeArea = useAppSelector(selectActiveArea);

  const mostImportantBanner = banners.find(
    (banner) => banner.shouldShow?.({ activeArea }) ?? true,
  );

  /** Callback to remove the most important banner from the stack (dismissed by the user) */
  const popBanner = useCallback(() => {
    setBanners((banners) =>
      banners.filter((banner) => banner !== mostImportantBanner),
    );
  }, [mostImportantBanner]);

  if (!mostImportantBanner) return null;

  return <Banner {...mostImportantBanner} onClose={popBanner} />;
}

/**
 * @param bannerA First banner to compare.
 * @param bannerB Second banner to compare.
 * @returns the ordering between two BannerProps to order first the most important one based on
 * the variant following the priority defines in the FARO design system
 */
function sortBanners(bannerA: BannerData, bannerB: BannerData): number {
  const VARIANT_PRIORITIES: BannerVariants[] = [
    "error",
    "warning",
    "info",
    "success",
  ];

  const bannerAVariantPriority = VARIANT_PRIORITIES.indexOf(bannerA.variant);
  const bannerBVariantPriority = VARIANT_PRIORITIES.indexOf(bannerB.variant);
  return bannerAVariantPriority === bannerBVariantPriority
    ? bannerA.priority - bannerB.priority
    : bannerAVariantPriority - bannerBVariantPriority;
}

/**
 * Append a Banner if needed to inform the user the project is outdated
 *
 * @param addBanner function used to add a new banner
 */
function useCheckForOutdatedProject(addBanner: AddBannerFunction): void {
  const projectStatus = useProjectStatus();

  const projectId = useAppSelector(selectProjectId);
  const [checkedProject, setCheckedProject] = useState<GUID>();

  useEffect(() => {
    if (checkedProject === projectId) return;
    if (projectStatus === ProjectStatus.outdated) {
      setCheckedProject(projectId);
      addBanner({
        bannerId: `outdated-${projectId}`,
        variant: "warning",
        priority: 2,
        children: PROJECT_OUTDATED_MESSAGE,
      });
    }
  }, [addBanner, checkedProject, projectId, projectStatus]);
}

/**
 * Append a Banner to reload the page if there's a newer merged revision than the one currently shown.
 *
 * @param addBanner function used to add a new banner
 */
function useCheckForNewMergedRevision(addBanner: AddBannerFunction): void {
  const callbackHasNewMergedRevision = useCallback((): void => {
    addBanner({
      bannerId: "new-merged-revision",
      variant: "warning",
      priority: 3,
      title: "New version available",
      children:
        "A new version of this project has been published. Please reload the page to view it.",
      action: {
        label: "Reload",
        action: () => window.location.reload(),
      },
    });
  }, [addBanner]);

  usePollNewMergedRevision(callbackHasNewMergedRevision);
}

/**
 * Append a Banner if needed to inform the user the PointCloudManagement module is missing
 *
 * @param addBanner function used to add a new banner
 */
function useCheckForMissingPCMBundle(addBanner: AddBannerFunction): void {
  const areSubscriptionsAvailable = useAppSelector(
    selectAreSubscriptionsAvailable,
  );
  const isMissingPCMBundle = useAppSelector(
    (state) =>
      !selectCanReadPointCloud(state) &&
      selectLoadedIElements(state).some(isIElementPointCloudStream),
  );
  const addonUrl = useDashboardAddOnUrl();
  const projectId = useAppSelector(selectProjectId);

  const checkedProject = useRef<GUID>(undefined);

  useEffect(() => {
    if (!areSubscriptionsAvailable || checkedProject.current === projectId) {
      return;
    }
    if (isMissingPCMBundle) {
      checkedProject.current = projectId;
      addBanner({
        bannerId: `missing-pcm-${projectId}`,
        variant: "warning",
        priority: 5,
        title: "Add-on required",
        children:
          "This project has point clouds. To visualize them, please purchase the point cloud Management add-on.",
        action: {
          label: "Manage Add Ons",
          action: addonUrl,
        },
      });
    }
  }, [
    addBanner,
    addonUrl,
    areSubscriptionsAvailable,
    isMissingPCMBundle,
    projectId,
  ]);
}

/**
 * Append a Banner if needed to inform the user the current floor has no scale information
 *
 * @param addBanner function used to add a new banner
 */
function useCheckForUnscaledFloor(addBanner: AddBannerFunction): void {
  const activeArea = useAppSelector(selectActiveArea);
  const isFloorWithoutScale = useAppSelector(
    selectIsFloorWithoutScale(activeArea),
  );
  const dispatch = useAppDispatch();
  const [checkedFloors, setCheckedFloors] = useState<GUID[]>([]);

  const disableScaling = useDisableCaptureTreeAlignment(activeArea);

  // Add a banner if the area section was not already checked, has no scale and the scale tool is available
  useEffect(() => {
    if (!activeArea || checkedFloors.includes(activeArea.id)) {
      return;
    }

    if (isFloorWithoutScale) {
      setCheckedFloors((floors) => [...floors, activeArea.id]);
      addBanner({
        bannerId: `unscaled-${activeArea.id}`,
        variant: "warning",
        priority: 4,
        title: "Sheet scale not defined.",
        children: `Data alignment and visual representation may not be accurate. ${disableScaling ? disableScaleAreaMessage : "Use the set scale tool in the three-dots menu."}`,
        action: disableScaling
          ? undefined
          : {
              label: "Set scale",
              action() {
                Analytics.track(EventType.openAreaScaleTool);
                dispatch(changeMode("floorscale"));
              },
            },
        shouldShow(context) {
          return context.activeArea?.id === activeArea.id;
        },
      });
    }
  }, [
    addBanner,
    checkedFloors,
    dispatch,
    activeArea,
    isFloorWithoutScale,
    disableScaling,
  ]);
}

/**
 * Append a Banner if needed to inform the user the current floor is empty and user can start adding data
 *
 * @param addBanner function used to add a new banner
 * @param removeBanner function used to remove a banner
 */
function useCheckForEmptyArea(
  addBanner: AddBannerFunction,
  removeBanner: RemoveBannerFunction,
): void {
  const activeArea = useCurrentAreaIfAvailable();
  const isAreaLoaded = useAppSelector(
    selectIsSubtreeLoaded(activeArea?.area?.id),
  );

  // This value also depends on the state of the loading of the datasets in the capture tree.
  // Since that loading is NOT blocking the application until it's finished, it might happen that
  // the banner code checking the datasets, is executed before the datasets are loaded.
  const isAreaDataLoading = useIsCurrentAreaLoading();

  const areaHasCaptureTreeDataSets = !!useAppSelector((state) =>
    selectAreaCaptureTreeDataSetIds(state, activeArea?.area?.id),
  )?.length;

  const isAreaEmpty =
    !areaHasCaptureTreeDataSets &&
    activeArea?.dataSessions.length === 0 &&
    activeArea.roomsSections.length === 0;

  const canShowBanner = !useIsReportToolActive();

  const [checkedAreas, setCheckedAreas] = useState<GUID[]>([]);

  // Add a banner if the area is empty
  useEffect(() => {
    const area = activeArea?.area;

    if (
      !area ||
      !isAreaLoaded ||
      isAreaDataLoading ||
      (checkedAreas.includes(area.id) && isAreaEmpty && canShowBanner)
    ) {
      return;
    }

    if (isAreaEmpty && canShowBanner) {
      setCheckedAreas((areas) => [...areas, area.id]);
      addBanner({
        bannerId: `empty-area-${area.id}`,
        variant: "info",
        priority: 0,
        title: "Empty sheet",
        children:
          "Start adding data to your sheet by clicking on the Import Button, by using our JobWalk app or Stream app or add 360s via drag & drop!",
      });
    } else if (checkedAreas.length > 0) {
      // Remove the banners for all the areas which are empty as the user has switched to an area with data
      checkedAreas.map((areaId) => removeBanner(`empty-area-${areaId}`));

      // Reset the checked areas which are empty in order to allow the banner to appear again if the user moved to an empty area later
      setCheckedAreas([]);
    }
  }, [
    addBanner,
    activeArea,
    isAreaLoaded,
    isAreaEmpty,
    checkedAreas,
    removeBanner,
    isAreaDataLoading,
    canShowBanner,
  ]);
}

/**
 * Append a Banner if needed to inform the user the link used to open the app is not valid
 *
 * @param addBanner function used to add a new banner
 */
function useCheckForValidSavedView(addBanner: AddBannerFunction): void {
  const isInvalidLink = useSavedView() instanceof Error;

  // Add a banner if the link is invalid
  useEffect(() => {
    if (isInvalidLink) {
      addBanner({
        bannerId: "invalid-share-link",
        variant: "warning",
        priority: 0,
        title: "Invalid Link",
        children:
          "The view you are trying to access does not exist or has been moved to another location",
      });
    }
  }, [addBanner, isInvalidLink]);
}

// The cookie length after which FARO internal users are warned in the app
// This value was determined experimentally by visiting different environments with all cookies accepted until the upload stops working.
// The uploads are the first requests to fail at ~6000 bytes of cookies.
const COOKIE_LENGTH_WARNING_THRESHOLD = 5000;

/**
 * Append a Banner if an internal test user has a lot of cookies. This should notify the user when we run into this frequent issue.
 *
 * @param addBanner function used to add a new banner
 */
function useCheckForTooManyCookies(addBanner: AddBannerFunction): void {
  const currentUser = useAppSelector(selectCurrentUser);

  const settingsUrl = useDashboardAccountUrl();

  useEffect(() => {
    const mail = currentUser?.email;

    if (
      mail &&
      isInternalUserEmail(mail) &&
      document.cookie.length > COOKIE_LENGTH_WARNING_THRESHOLD
    ) {
      addBanner({
        bannerId: "too-many-cookies",
        variant: "warning",
        priority: 1,
        title: "Cookie Warning",
        children: (
          <Stack>
            <FaroText variant="bodyM">
              Please clear your cookies to avoid network issues.
            </FaroText>
            <FaroText variant="bodyXS">
              Note: This warning is only shown for FARO internal users who visit
              a lot of test environments. If possible, only accept the necessary
              cookies in your{" "}
              <Link href={settingsUrl} target="_blank">
                Account & Security
              </Link>{" "}
              settings.
            </FaroText>
          </Stack>
        ),
      });
    }
  }, [addBanner, currentUser?.email, settingsUrl]);
}

/**
 * Append a Banner if there's a draft revision of the project in progress.
 *
 * @param addBanner function used to add a new banner
 */
function useCheckForDraftInProgress(addBanner: AddBannerFunction): void {
  const projectId = useAppSelector(selectProjectId);

  const bannerProps = useDraftInProgressBannerProps(projectId);
  const hasDraft = useHasDraftInProgress(projectId);

  useEffect(() => {
    if (!hasDraft) return;

    addBanner({
      bannerId: "draft-in-progress",
      priority: 1,
      ...bannerProps,
    });
  }, [addBanner, bannerProps, hasDraft]);
}
